import React, { useState } from "react";
import { API_URL } from "../api";

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    success: false,
    message: "",
    details: "",
    sending: false,
  });

  const openMailFallback = () => {
    const subject = encodeURIComponent(`Website message from ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\n${formData.message}`,
    );
    window.location.href = `mailto:info@ohmycar.org?subject=${subject}&body=${body}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 30000);

    setFormStatus({
      submitted: false,
      error: false,
      success: false,
      message: "",
      details: "",
      sending: true,
    });

    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
        signal: controller.signal,
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Could not send the message.");
      }

      setFormData({ name: "", email: "", message: "" });
      setFormStatus({
        submitted: true,
        error: false,
        success: true,
        message: "Thank you for your message! We will get back to you soon.",
        details: "",
        sending: false,
      });
    } catch (error) {
      console.error("There was an error sending the email:", error);
      const details =
        error.name === "AbortError"
          ? "The email request timed out. Please try again in a moment."
          : error.message;
      setFormStatus({
        submitted: true,
        error: true,
        success: false,
        message:
          "We could not send the message automatically. You can open your email app and send it directly.",
        details,
        sending: false,
      });
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  return (
    <main className="contactPage">
      <section className="contactHero">
        <p>Contact Oh My Car</p>
        <h1>Need help choosing a car?</h1>
        <span>
          Send a message or contact us directly. We will help you arrange the
          right rental in Dubai.
        </span>
      </section>

      <section className="contactLayout">
        <div className="contactCards" aria-label="Contact information">
          <a className="contactCard" href="tel:+971529497677">
            <span>TEL</span>
            <div>
              <strong>Contact person</strong>
              <p>+971 52 949 7677</p>
            </div>
          </a>
          <a
            className="contactCard"
            href="https://api.whatsapp.com/send?phone=971529497677"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>WA</span>
            <div>
              <strong>WhatsApp</strong>
              <p>Fast booking support</p>
            </div>
          </a>
          <a className="contactCard" href="tel:+97145726930">
            <span>TEL</span>
            <div>
              <strong>Complaints</strong>
              <p>+971 4 572 6930</p>
            </div>
          </a>
          <a className="contactCard" href="mailto:info@ohmycar.org">
            <span>@</span>
            <div>
              <strong>Email</strong>
              <p>info@ohmycar.org</p>
            </div>
          </a>
          <div className="contactCard">
            <span>DXB</span>
            <div>
              <strong>Location</strong>
              <p>Ras Al Khor - Industrial 2, Dubai, UAE</p>
            </div>
          </div>
        </div>

        <div className="contact-container">
          <div className="formDiv">
            <h2>Send a message</h2>
            <p>
              Share your question and we will get back to you as soon as
              possible.
            </p>

            {formStatus.submitted && (
              <div
                className={`alert ${
                  formStatus.error ? "alert-danger" : "alert-success"
                }`}
              >
                {formStatus.message}
                {formStatus.details && (
                  <small className="contactErrorDetails">
                    Error: {formStatus.details}
                  </small>
                )}
                {formStatus.error && (
                  <button
                    type="button"
                    className="contactFallbackButton"
                    onClick={openMailFallback}
                  >
                    Open email app
                  </button>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-control"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  className="form-control"
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={formStatus.sending}
              >
                {formStatus.sending ? "Sending..." : "Send message"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="map">
        <h2>Our location</h2>

        <iframe
          src="https://maps.google.com/maps?q=Ras%20Al%20Khor%20Industrial%202%2C%20Dubai%2C%20UAE&t=&z=14&ie=UTF8&iwloc=&output=embed"
          width="600"
          height="450"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Company Location"
        ></iframe>
      </section>
    </main>
  );
}

export default Contact;
